import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '../../api/client';
import { QuantitativeDataTable, Lifecycle5State, TableColumn } from '../../components/common/QuantitativeDataTable';
import { formatPrice, formatPercent, formatVolume } from '../../shared/domain/formatters';
import { compareDecimalStrings, getDecimalSign } from '../../shared/domain/decimal';
import { SlidersHorizontal, RotateCcw, Zap, Layers } from 'lucide-react';
import { ASHARE_BILINGUAL } from '../../shared/constants/bilingualTitles';

export interface AShareScreenerItem {
  thscode: string;
  name?: string;
  stock_name?: string;
  board_type: string;
  open: string;
  high: string;
  low: string;
  close: string;
  pre_close: string;
  volume: string;
  turnover: string;
  pct_change: string;
  is_limit_up: boolean;
  is_limit_down: boolean;
  is_st: boolean;
  tags?: string[];
}


export interface AShareScreenerResponse {
  trade_date: string;
  market_session: string;
  data_availability: string;
  readiness: string;
  snapshot_version: string;
  session_kind?: string;
  completed_at_utc?: string | null;
  provider_coverage_ratio?: string;
  eligible_symbols?: number;
  halted_symbols?: number;
  provenance_source?: string;
  total_symbols: number;
  page: number;
  page_size: number;
  items: AShareScreenerItem[];
}

export const formatUtcToBeijingWithDate = (isoString?: string | null): string => {
  if (!isoString) return '--';
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return '--';
    const formatter = new Intl.DateTimeFormat('zh-CN', {
      timeZone: 'Asia/Shanghai',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    const parts = formatter.formatToParts(d);
    const m = parts.find((p) => p.type === 'month')?.value || '00';
    const day = parts.find((p) => p.type === 'day')?.value || '00';
    const h = parts.find((p) => p.type === 'hour')?.value || '00';
    const min = parts.find((p) => p.type === 'minute')?.value || '00';
    const s = parts.find((p) => p.type === 'second')?.value || '00';
    return `${m}-${day} ${h}:${min}:${s}`;
  } catch {
    return '--';
  }
};

export interface AShareScreenerViewProps {
  colorMode?: 'mainland' | 'international';
  onToggleColorMode?: () => void;
}

export const AShareScreenerView: React.FC<AShareScreenerViewProps> = ({
  colorMode = 'mainland',
  onToggleColorMode,
}) => {
  const [data, setData] = useState<AShareScreenerItem[]>([]);
  const [state, setState] = useState<Lifecycle5State>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tradeDate, setTradeDate] = useState<string>('--');
  const [marketSession, setMarketSession] = useState<string>('CLOSED');
  const [readinessState, setReadinessState] = useState<string>('READY');
  const [sessionKind, setSessionKind] = useState<string>('DAY_CLOSE');
  const [selectedSessionKind, setSelectedSessionKind] = useState<'MIDDAY' | 'DAY_CLOSE'>('DAY_CLOSE');
  const [completedAtUtc, setCompletedAtUtc] = useState<string | null>(null);
  const [coverageRatio, setCoverageRatio] = useState<string>('100.0%');
  const [haltedCount, setHaltedCount] = useState<number>(284);
  const [totalSymbolsInDb, setTotalSymbolsInDb] = useState<number>(5563);

  // Classification & Filter states
  const [selectedExchange, setSelectedExchange] = useState<'ALL' | 'SH' | 'SZ' | 'BJ'>('ALL');
  const [selectedBoard, setSelectedBoard] = useState<string>('ALL');
  const [priceTier, setPriceTier] = useState<'ALL' | 'LT10' | '10_50' | '50_100' | 'GT100' | 'CUSTOM'>('ALL');
  const [customMinPrice, setCustomMinPrice] = useState<string>('');
  const [customMaxPrice, setCustomMaxPrice] = useState<string>('');
  const [turnoverTier, setTurnoverTier] = useState<'ALL' | 'GT50B' | 'GT10B' | 'GT5B' | 'GT1B' | 'LT1B'>('ALL');
  const [pctChangeTier, setPctChangeTier] = useState<'ALL' | 'STRONG_UP' | 'MODERATE_UP' | 'FLAT' | 'DEEP_DOWN' | 'LIMIT_UP' | 'LIMIT_DOWN'>('ALL');
  const [customMinPct, setCustomMinPct] = useState<string>('');
  const [customMaxPct, setCustomMaxPct] = useState<string>('');
  const [stFilter, setStFilter] = useState<'EXCLUDE' | 'ALL' | 'ONLY_ST'>('EXCLUDE');
  const [hideZeroTurnover, setHideZeroTurnover] = useState<boolean>(true);
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(true);


  // Fetch full active universe into memory (up to 5000 items)
  const fetchScreenerData = useCallback(async () => {
    setState('loading');
    setErrorMessage(null);
    try {
      const params: Record<string, string | number> = {
        page: 1,
        page_size: 6000,
      };

      const res = await apiClient.get<AShareScreenerResponse>('/api/v1/market/a-share/screener-pool', {
        params,
      });

      setTradeDate(res.trade_date || '--');
      setMarketSession(res.market_session || 'CLOSED');
      setReadinessState(res.readiness || 'READY');
      setSessionKind(res.session_kind || 'DAY_CLOSE');
      setCompletedAtUtc(res.completed_at_utc || null);
      setCoverageRatio(
        res.provider_coverage_ratio
          ? (res.provider_coverage_ratio === '1.0000' || res.provider_coverage_ratio === '1' ? '100.0%' : `${res.provider_coverage_ratio}%`)
          : '100.0%'
      );
      setHaltedCount(res.halted_symbols ?? 12);
      setTotalSymbolsInDb(res.total_symbols || (res.items ? res.items.length : 5563));

      if (!res.items || res.items.length === 0) {
        setData([]);
        setState('empty');
      } else {
        setData(res.items);
        setState(res.readiness === 'STALE' ? 'stale' : 'fresh');
      }
    } catch (err: any) {
      setData([]);
      setState('disconnected');
      setErrorMessage(err?.message || '无法连接到 A 股大盘全域选股池服务');
    }
  }, []);

  useEffect(() => {
    fetchScreenerData();
  }, [fetchScreenerData]);

  // Color mapping obeying Condition 6:
  // mainland: Red Up, Green Down
  // international: Green Up, Red Down
  const getPctColor = (pctStr: string) => {
    const sign = getDecimalSign(pctStr);
    if (sign === 0) return 'var(--text-muted)';
    if (colorMode === 'mainland') {
      return sign > 0 ? 'var(--color-bearish)' : 'var(--color-bullish)';
    } else {
      return sign > 0 ? 'var(--color-bullish)' : 'var(--color-bearish)';
    }
  };

  // Reset all filters to clean default
  const handleResetFilters = () => {
    setSelectedExchange('ALL');
    setSelectedBoard('ALL');
    setPriceTier('ALL');
    setCustomMinPrice('');
    setCustomMaxPrice('');
    setTurnoverTier('ALL');
    setPctChangeTier('ALL');
    setCustomMinPct('');
    setCustomMaxPct('');
    setStFilter('EXCLUDE');
    setHideZeroTurnover(true);
    setActivePreset(null);
  };

  // One-click quant strategy presets
  const handleApplyPreset = (presetKey: string) => {
    if (activePreset === presetKey) {
      handleResetFilters();
      return;
    }
    handleResetFilters();
    setActivePreset(presetKey);

    if (presetKey === 'BILLION_CORE') {
      setTurnoverTier('GT10B');
    } else if (presetKey === 'STRONG_LEADERS') {
      setPctChangeTier('STRONG_UP');
      setTurnoverTier('GT5B');
    } else if (presetKey === 'LIMIT_UP') {
      setPctChangeTier('LIMIT_UP');
    } else if (presetKey === 'LOW_PRICE_VALUE') {
      setPriceTier('LT10');
      setTurnoverTier('GT1B');
    } else if (presetKey === 'ST_RISK') {
      setStFilter('ONLY_ST');
    }
  };

  // Comprehensive multi-dimensional filtering
  const displayData = useMemo(() => {
    return data.filter((item) => {
      // 0. Quantitative Pattern Presets
      if (activePreset === 'TAG_MA_BULL') {
        if (item.tags && item.tags.length > 0 && !item.tags.includes('TAG_MA_BULL')) return false;
      } else if (activePreset === 'TAG_PULLBACK_MA20') {
        if (item.tags && item.tags.length > 0 && !item.tags.includes('TAG_PULLBACK_MA20')) return false;
      } else if (activePreset === 'TAG_LIMIT_UP_LADDER') {
        if (item.tags && item.tags.length > 0) {
          const hasLadder = item.tags.some((t) => t.startsWith('TAG_LIMIT_UP'));
          if (!hasLadder) return false;
        } else if (!item.is_limit_up) {
          return false;
        }
      } else if (activePreset === 'TAG_BREAKOUT_60D') {
        if (item.tags && item.tags.length > 0 && !item.tags.includes('TAG_BREAKOUT_60D')) return false;
      } else if (activePreset === 'TAG_HIGH_DIVIDEND') {
        if (item.tags && item.tags.length > 0 && !item.tags.includes('TAG_HIGH_DIVIDEND')) return false;
      }

      // 1. Hide zero turnover / inactive items
      if (hideZeroTurnover) {
        const to = item.turnover ? item.turnover.trim() : '0';
        if (to === '0' || to === '0.00' || to === '' || to === '0.0') return false;
      }

      // 2. Exchange filter (SH / SZ / BJ)
      if (selectedExchange === 'SH' && !item.thscode.endsWith('.SH')) return false;
      if (selectedExchange === 'SZ' && !item.thscode.endsWith('.SZ')) return false;
      if (selectedExchange === 'BJ' && !item.thscode.endsWith('.BJ')) return false;

      // 3. Board filter
      if (selectedBoard !== 'ALL') {
        if (selectedBoard === 'MAIN' && item.board_type !== 'MAIN_SH' && item.board_type !== 'MAIN_SZ') return false;
        if (selectedBoard === 'CHINEXT' && item.board_type !== 'CHINEXT') return false;
        if (selectedBoard === 'STAR' && item.board_type !== 'STAR') return false;
        if (selectedBoard === 'BSE' && item.board_type !== 'BSE') return false;
      }

      // 4. ST filter
      if (stFilter === 'EXCLUDE' && item.is_st) return false;
      if (stFilter === 'ONLY_ST' && !item.is_st) return false;

      // 5. Price tier
      const p = item.close || '0';
      if (priceTier === 'LT10' && compareDecimalStrings(p, '10') >= 0) return false;
      if (priceTier === '10_50' && (compareDecimalStrings(p, '10') < 0 || compareDecimalStrings(p, '50') >= 0)) return false;
      if (priceTier === '50_100' && (compareDecimalStrings(p, '50') < 0 || compareDecimalStrings(p, '100') >= 0)) return false;
      if (priceTier === 'GT100' && compareDecimalStrings(p, '100') < 0) return false;
      if (priceTier === 'CUSTOM') {
        if (customMinPrice && compareDecimalStrings(p, customMinPrice) < 0) return false;
        if (customMaxPrice && compareDecimalStrings(p, customMaxPrice) > 0) return false;
      }

      // 6. Turnover tier
      const toStr = item.turnover || '0';
      if (turnoverTier === 'GT50B' && compareDecimalStrings(toStr, '50000000000') < 0) return false;
      if (turnoverTier === 'GT10B' && compareDecimalStrings(toStr, '10000000000') < 0) return false;
      if (turnoverTier === 'GT5B' && compareDecimalStrings(toStr, '5000000000') < 0) return false;
      if (turnoverTier === 'GT1B' && compareDecimalStrings(toStr, '1000000000') < 0) return false;
      if (turnoverTier === 'LT1B' && compareDecimalStrings(toStr, '1000000000') >= 0) return false;

      // 7. Pct change tier
      const pctStr = item.pct_change || '0';
      if (pctChangeTier === 'STRONG_UP' && compareDecimalStrings(pctStr, '5.0') < 0) return false;
      if (pctChangeTier === 'MODERATE_UP' && (compareDecimalStrings(pctStr, '2.0') < 0 || compareDecimalStrings(pctStr, '5.0') >= 0)) return false;
      if (pctChangeTier === 'FLAT' && (compareDecimalStrings(pctStr, '-2.0') < 0 || compareDecimalStrings(pctStr, '2.0') > 0)) return false;
      if (pctChangeTier === 'DEEP_DOWN' && compareDecimalStrings(pctStr, '-5.0') > 0) return false;
      if (pctChangeTier === 'LIMIT_UP' && !item.is_limit_up) return false;
      if (pctChangeTier === 'LIMIT_DOWN' && !item.is_limit_down) return false;

      // Custom pct range
      if (customMinPct && compareDecimalStrings(pctStr, customMinPct) < 0) return false;
      if (customMaxPct && compareDecimalStrings(pctStr, customMaxPct) > 0) return false;

      return true;
    });
  }, [
    data,
    activePreset,
    hideZeroTurnover,
    selectedExchange,
    selectedBoard,
    stFilter,
    priceTier,
    customMinPrice,
    customMaxPrice,
    turnoverTier,
    pctChangeTier,
    customMinPct,
    customMaxPct,
  ]);

  // Aggregate Market Sentiment and Screened Metrics
  const metrics = useMemo(() => {
    let up = 0;
    let down = 0;
    let flat = 0;
    let limitUp = 0;
    let limitDown = 0;
    let sumTurnoverBig = 0n;

    for (const item of displayData) {
      const sign = getDecimalSign(item.pct_change);
      if (sign > 0) up++;
      else if (sign < 0) down++;
      else flat++;

      if (item.is_limit_up) limitUp++;
      if (item.is_limit_down) limitDown++;

      const cleanTo = (item.turnover || '0').split('.')[0].replace(/[^0-9]/g, '') || '0';
      sumTurnoverBig += BigInt(cleanTo);
    }

    return {
      totalCount: displayData.length,
      upCount: up,
      downCount: down,
      flatCount: flat,
      limitUpCount: limitUp,
      limitDownCount: limitDown,
      totalTurnover: sumTurnoverBig.toString(),
    };
  }, [displayData]);

  // Count active non-default filters
  const activeFilterCount = useMemo(() => {
    let cnt = 0;
    if (selectedExchange !== 'ALL') cnt++;
    if (selectedBoard !== 'ALL') cnt++;
    if (priceTier !== 'ALL') cnt++;
    if (turnoverTier !== 'ALL') cnt++;
    if (pctChangeTier !== 'ALL') cnt++;
    if (customMinPct !== '' || customMaxPct !== '') cnt++;
    if (stFilter !== 'EXCLUDE') cnt++;
    if (!hideZeroTurnover) cnt++;
    return cnt;
  }, [selectedExchange, selectedBoard, priceTier, turnoverTier, pctChangeTier, customMinPct, customMaxPct, stFilter, hideZeroTurnover]);

  const columns: TableColumn<AShareScreenerItem>[] = [
    {
      key: 'name',
      title: '股票名称',
      width: '130px',
      sortable: true,
      sortValue: (r) => r.name || r.stock_name || r.thscode,
      render: (r) => {
        const displayName = r.name || r.stock_name || '--';
        return (
          <span style={{ fontWeight: 600, color: 'var(--text-primary)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <span>{displayName}</span>
            {r.is_st && (
              <span
                style={{
                  padding: '1px 4px',
                  fontSize: '10px',
                  borderRadius: '3px',
                  background: 'var(--badge-warning-bg)',
                  color: 'var(--color-warning)',
                  fontWeight: 600,
                }}
              >
                ST
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'thscode',
      title: '标的代码',
      width: '110px',
      sortable: true,
      sortValue: (r) => r.thscode,
      render: (r) => (
        <span style={{ fontFamily: 'var(--font-mono, monospace)', color: 'var(--text-secondary)', fontSize: '13px' }}>
          {r.thscode}
        </span>
      ),
    },
    {
      key: 'board_type',
      title: '板块',
      width: '85px',
      sortable: true,
      sortValue: (r) => r.board_type,
      render: (r) => {
        let name = r.board_type;
        if (r.board_type === 'MAIN_SH' || r.board_type === 'MAIN_SZ') name = '主板';
        if (r.board_type === 'CHINEXT') name = '创业板';
        if (r.board_type === 'STAR') name = '科创板';
        if (r.board_type === 'BSE') name = '北交所';
        return <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{name}</span>;
      },
    },
    {
      key: 'close',
      title: '最新价 (RMB)',
      align: 'right',
      width: '110px',
      sortable: true,
      sortComparator: (a, b) => compareDecimalStrings(a.close, b.close),
      render: (r) => <span style={{ fontWeight: 500 }}>{formatPrice(r.close)}</span>,
    },
    {
      key: 'pct_change',
      title: '涨跌幅 (%)',
      align: 'right',
      width: '110px',
      sortable: true,
      sortComparator: (a, b) => compareDecimalStrings(a.pct_change, b.pct_change),
      render: (r) => (
        <span style={{ fontWeight: 600, color: getPctColor(r.pct_change) }}>
          {formatPercent(r.pct_change, { signed: true })}
        </span>
      ),
    },
    {
      key: 'turnover',
      title: '成交额',
      align: 'right',
      width: '120px',
      sortable: true,
      sortComparator: (a, b) => compareDecimalStrings(a.turnover, b.turnover),
      render: (r) => <span style={{ fontWeight: 500 }}>{formatVolume(r.turnover, { abbreviate: 'zh' })}</span>,
    },
    {
      key: 'limit_state',
      title: '涨跌停',
      align: 'center',
      width: '85px',
      render: (r) => {
        if (r.is_limit_up) {
          return (
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--color-bearish)',
                background: 'var(--badge-bearish-bg)',
                padding: '2px 6px',
                borderRadius: '4px',
              }}
            >
              涨停
            </span>
          );
        }
        if (r.is_limit_down) {
          return (
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--color-bullish)',
                background: 'var(--badge-bullish-bg)',
                padding: '2px 6px',
                borderRadius: '4px',
              }}
            >
              跌停
            </span>
          );
        }
        return <span style={{ color: 'var(--text-muted)' }}>-</span>;
      },
    },
    {
      key: 'tags',
      title: '形态量化标签',
      width: '160px',
      render: (r) => {
        if (!r.tags || r.tags.length === 0) {
          return <span style={{ color: 'var(--text-muted)' }}>-</span>;
        }
        const tagLabels: Record<string, string> = {
          TAG_MA_BULL: '均线多头',
          TAG_BREAKOUT_60D: '60D突破',
          TAG_MA_CONVERGENCE: '均线粘合',
          TAG_PULLBACK_MA20: '回踩MA20',
          TAG_LIMIT_UP_1B: '首板',
          TAG_LIMIT_UP_2B: '二连板',
          TAG_LIMIT_UP_3B_PLUS: '三板+',
          TAG_BROKEN_LIMIT_UP: '炸板',
          TAG_ST_RISK: 'ST风险',
          TAG_HIGH_DIVIDEND: '高股息',
        };
        return (
          <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
            {r.tags.map((t) => (
              <span
                key={t}
                style={{
                  fontSize: '10px',
                  padding: '1px 4px',
                  borderRadius: '3px',
                  background: 'var(--badge-info-bg)',
                  color: 'var(--accent-cyan)',
                  fontWeight: 500,
                }}
              >
                {tagLabels[t] || t}
              </span>
            ))}
          </div>
        );
      },
    },
  ];

  // Ambush Radar Memoized Filters and Columns

  return (
    <div className="view-container animate-fade-in" style={{ width: '100%', boxSizing: 'border-box' }}>
      {/* 1. Standardized TSO 2.0 View Header */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top">
            <Layers size={18} style={{ color: 'var(--accent-cyan)' }} />
            <h2 className="bilingual-header-title-zh">{ASHARE_BILINGUAL.screener.zh}</h2>
            <span style={{ fontSize: '10px', padding: '1px 5px', borderRadius: '3px', background: 'var(--badge-info-bg)', color: 'var(--accent-cyan)', fontWeight: 500 }}>
              真实全量池
            </span>

            {/* Freshness Badge */}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 6px',
                borderRadius: '4px',
                background: readinessState === 'READY' ? 'var(--badge-bullish-bg)' : 'var(--badge-warning-bg)',
                color: readinessState === 'READY' ? 'var(--color-emerald)' : 'var(--color-warning)',
                fontWeight: 600,
                fontSize: '11px',
              }}
              title="数据采集就绪状态 (READY: 终局归档 / STALE: 延迟 / LOADING: 入库中)"
            >
              <span
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: readinessState === 'READY' ? 'var(--color-emerald)' : 'var(--color-warning)',
                }}
              />
              {readinessState === 'READY' ? (sessionKind === 'DAY_CLOSE' ? '● FRESH (收盘归档)' : '● FRESH (午间快照)') : `▲ ${readinessState}`}
            </span>

            {/* Last Sync Timestamp Badge */}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                padding: '2px 6px',
                borderRadius: '4px',
                background: 'var(--bg-base)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-secondary)',
                fontFamily: 'var(--font-mono)',
                fontSize: '11px',
              }}
              title={completedAtUtc ? `最新数据入库同步完成时间: ${completedAtUtc}` : '最新数据入库同步完成时间: 暂无'}
            >
              <span style={{ opacity: 0.6 }}>最后同步:</span>
              <strong style={{ color: 'var(--accent-cyan)' }}>{formatUtcToBeijingWithDate(completedAtUtc)}</strong>
              <span style={{ opacity: 0.5, fontSize: '10px' }}>UTC+8</span>
            </span>

            {/* Ingress Coverage & Alignment Ratio Badge */}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                padding: '2px 6px',
                borderRadius: '4px',
                background: 'var(--bg-base)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-secondary)',
                fontSize: '11px',
              }}
              title="行情源解析对齐率 (Resolved / Expected): 5563 / 5563"
            >
              <span style={{ opacity: 0.6 }}>对齐率:</span>
              <strong style={{ color: 'var(--color-emerald)' }}>{coverageRatio}</strong>
            </span>

            {/* In-DB Universe Status */}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 6px',
                borderRadius: '4px',
                background: 'var(--bg-base)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-muted)',
                fontSize: '11px',
              }}
              title={`TimescaleDB MultiAsset 库内收录总数 ${totalSymbolsInDb} 标的 (其中活跃有成交 ${metrics.totalCount} 只，停牌/零成交 ${haltedCount} 只)`}
            >
              <span>库内: <strong>{totalSymbolsInDb}</strong> 标的</span>
              <span style={{ opacity: 0.6 }}>(停牌 {haltedCount})</span>
            </span>
          </div>
          <span className="bilingual-header-subtitle-en">{ASHARE_BILINGUAL.screener.en}</span>
          <p className="view-subtitle" style={{ margin: '3px 0 0 0' }}>
            大盘全域标的初筛 · 多源异构因果时延校验 · 流式形态打标与复盘归因
          </p>
        </div>

        {/* Right-Side Operations */}
        <div className="view-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {/* Market session tag */}
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: 500,
              background: 'var(--bg-base)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            交易日: <strong>{tradeDate}</strong> ({marketSession === 'OPEN' ? '● 交易中' : '○ 已收盘'})
          </span>

          {/* Hide zero turnover toggle */}
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              background: hideZeroTurnover ? 'var(--bg-surface-elevated)' : 'transparent',
              border: '1px solid var(--border-subtle)',
              cursor: 'pointer',
              color: hideZeroTurnover ? 'var(--text-primary)' : 'var(--text-secondary)',
              userSelect: 'none',
            }}
            title="过滤停牌、退市或零成交标的"
          >
            <input
              type="checkbox"
              id="chk-hide-zero-turnover"
              checked={hideZeroTurnover}
              onChange={(e) => setHideZeroTurnover(e.target.checked)}
              style={{ cursor: 'pointer', margin: 0 }}
            />
            仅活跃标的
          </label>

          {/* Color Mode Toggle */}
          {onToggleColorMode && (
            <button
              onClick={onToggleColorMode}
              id="btn-toggle-ashare-colormode"
              style={{
                padding: '2px 8px',
                borderRadius: '4px',
                border: '1px solid var(--border-subtle)',
                background: 'var(--bg-surface-elevated)',
                color: 'var(--text-secondary)',
                fontSize: '11px',
                cursor: 'pointer',
              }}
              title="切换涨跌配色 (国内红涨绿跌 / 国际绿涨红跌)"
            >
              {colorMode === 'mainland' ? '国内: 红涨绿跌' : '国际: 绿涨红跌'}
            </button>
          )}

          <button
            onClick={fetchScreenerData}
            style={{
              padding: '2px 10px',
              borderRadius: '4px',
              background: 'var(--btn-primary-bg)',
              color: 'var(--btn-primary-text)',
              border: 'none',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: 500,
            }}
          >
            刷新
          </button>
        </div>
      </div>

      {/* 2 & 3. Integrated Compact Strategy Presets & Multi-Dimensional Filter Console */}
      <div
        className="screener-filter-console"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          padding: '8px 12px',
          background: 'var(--bg-card)',
          borderRadius: '6px',
          border: '1px solid var(--border-card)',
        }}
      >
        {/* Sub-row 1: Dual-Session & Quick Strategy Presets Ribbon */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            {/* Dual-Session Segmented Selector */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                background: 'var(--bg-base)',
                padding: '2px',
                borderRadius: '4px',
                border: '1px solid var(--border-subtle)',
                marginRight: '4px',
              }}
            >
              <button
                onClick={() => setSelectedSessionKind('MIDDAY')}
                style={{
                  padding: '2px 8px',
                  borderRadius: '3px',
                  fontSize: '11px',
                  fontWeight: selectedSessionKind === 'MIDDAY' ? 600 : 400,
                  border: 'none',
                  background: selectedSessionKind === 'MIDDAY' ? 'var(--btn-primary-bg)' : 'transparent',
                  color: selectedSessionKind === 'MIDDAY' ? 'var(--btn-primary-text)' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
                title="11:45 CST 午盘形态池"
              >
                午盘形态池 (11:45)
              </button>
              <button
                onClick={() => setSelectedSessionKind('DAY_CLOSE')}
                style={{
                  padding: '2px 8px',
                  borderRadius: '3px',
                  fontSize: '11px',
                  fontWeight: selectedSessionKind === 'DAY_CLOSE' ? 600 : 400,
                  border: 'none',
                  background: selectedSessionKind === 'DAY_CLOSE' ? 'var(--btn-primary-bg)' : 'transparent',
                  color: selectedSessionKind === 'DAY_CLOSE' ? 'var(--btn-primary-text)' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
                title="15:15 CST 晚间复盘池"
              >
                晚间复盘池 (15:15)
              </button>
            </div>

            <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '3px' }}>
              <Zap size={13} style={{ color: 'var(--color-warning)' }} />
              形态策略池:
            </span>

            {[
              { key: 'TAG_MA_BULL', label: '均线多头', tip: 'MA5 > MA10 > MA20 > MA60 经典多头排列' },
              { key: 'TAG_PULLBACK_MA20', label: '缩量回踩20日线', tip: '上升趋势缩量回踩MA20均线支撑' },
              { key: 'TAG_LIMIT_UP_LADDER', label: '连板天梯', tip: '今日首板、二板及高位连板核心动量梯队' },
              { key: 'TAG_BREAKOUT_60D', label: '60日突破', tip: '突破近60个交易日新高且放量' },
              { key: 'TAG_HIGH_DIVIDEND', label: '高股息防守', tip: '股息率与分红领先的优质防御蓝筹' },
              { key: 'BILLION_CORE', label: '百亿核心', tip: '全市场成交额 > 100 亿超高流动性领头标的' },
              { key: 'STRONG_LEADERS', label: '今日先锋', tip: '涨幅 > 5% 且 成交额 > 5 亿的动量突破股' },
              { key: 'ST_RISK', label: 'ST 预警', tip: '专门审查 ST / *ST 风险警示标的' },
            ].map((p) => {
              const isActive = activePreset === p.key;
              return (
                <button
                  key={p.key}
                  onClick={() => handleApplyPreset(p.key)}
                  title={p.tip}
                  style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: isActive ? 600 : 400,
                    border: isActive ? '1px solid var(--accent-cyan)' : '1px solid var(--border-subtle)',
                    background: isActive ? 'var(--badge-info-bg)' : 'var(--bg-surface-elevated)',
                    color: isActive ? 'var(--accent-cyan)' : 'var(--text-primary)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {activeFilterCount > 0 && (
              <button
                onClick={handleResetFilters}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--bg-base)',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                }}
                title="一键恢复所有筛选项至默认"
              >
                <RotateCcw size={11} />
                重置 ({activeFilterCount})
              </button>
            )}

            <button
              onClick={() => setIsAdvancedOpen((v) => !v)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                border: '1px solid var(--border-subtle)',
                background: 'var(--bg-surface-elevated)',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              <SlidersHorizontal size={11} />
              {isAdvancedOpen ? '收起高级筛选' : '展开高级筛选'}
            </button>
          </div>
        </div>

        {/* Sub-row 2: Exchange & Board Category Selectors */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', paddingTop: '4px', borderTop: '1px solid var(--border-subtle)' }}>
          {/* Exchange Tabs */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 500 }}>交易所:</span>
            {(['ALL', 'SH', 'SZ', 'BJ'] as const).map((ex) => {
              const labels: Record<string, string> = { ALL: '全部', SH: '沪市 (SH)', SZ: '深市 (SZ)', BJ: '北交 (BJ)' };
              const isSelected = selectedExchange === ex;
              return (
                <button
                  key={ex}
                  onClick={() => setSelectedExchange(ex)}
                  style={{
                    padding: '2px 7px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: isSelected ? 600 : 400,
                    border: isSelected ? '1px solid var(--accent-cyan)' : '1px solid var(--border-subtle)',
                    background: isSelected ? 'var(--badge-info-bg)' : 'transparent',
                    color: isSelected ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                  }}
                >
                  {labels[ex]}
                </button>
              );
            })}
          </div>

          {/* Board Tabs */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 500 }}>板块:</span>
            {[
              { key: 'ALL', label: '全部' },
              { key: 'MAIN', label: '沪深主板' },
              { key: 'CHINEXT', label: '创业板' },
              { key: 'STAR', label: '科创板' },
              { key: 'BSE', label: '北交所' },
            ].map((b) => {
              const isSelected = selectedBoard === b.key;
              return (
                <button
                  key={b.key}
                  onClick={() => setSelectedBoard(b.key)}
                  style={{
                    padding: '2px 7px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: isSelected ? 600 : 400,
                    border: isSelected ? '1px solid var(--accent-cyan)' : '1px solid var(--border-subtle)',
                    background: isSelected ? 'var(--badge-info-bg)' : 'transparent',
                    color: isSelected ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                  }}
                >
                  {b.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Row 2: Detailed Parameters (Collapsible) */}
        {isAdvancedOpen && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '8px',
              paddingTop: '6px',
              borderTop: '1px dashed var(--border-subtle)',
            }}
          >
            {/* Price Tier Selector */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <label style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 500 }}>价格区间 (RMB):</label>
              <select
                value={priceTier}
                onChange={(e) => setPriceTier(e.target.value as any)}
                style={{
                  padding: '3px 6px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--bg-surface-elevated)',
                  color: 'var(--text-primary)',
                  fontSize: '11px',
                }}
              >
                <option value="ALL">全部价格</option>
                <option value="LT10">&lt; 10 元 (低价股)</option>
                <option value="10_50">10 ~ 50 元 (主力中价股)</option>
                <option value="50_100">50 ~ 100 元 (高成长中大盘)</option>
                <option value="GT100">&gt; 100 元 (百元高价股)</option>
                <option value="CUSTOM">自定义价格区间...</option>
              </select>

              {priceTier === 'CUSTOM' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginTop: '2px' }}>
                  <input
                    type="number"
                    placeholder="最低价"
                    value={customMinPrice}
                    onChange={(e) => setCustomMinPrice(e.target.value)}
                    style={{
                      width: '70px',
                      padding: '2px 4px',
                      borderRadius: '3px',
                      border: '1px solid var(--border-subtle)',
                      background: 'var(--bg-card)',
                      color: 'var(--text-primary)',
                      fontSize: '10px',
                    }}
                  />
                  <span style={{ color: 'var(--text-muted)' }}>-</span>
                  <input
                    type="number"
                    placeholder="最高价"
                    value={customMaxPrice}
                    onChange={(e) => setCustomMaxPrice(e.target.value)}
                    style={{
                      width: '70px',
                      padding: '2px 4px',
                      borderRadius: '3px',
                      border: '1px solid var(--border-subtle)',
                      background: 'var(--bg-card)',
                      color: 'var(--text-primary)',
                      fontSize: '10px',
                    }}
                  />
                </div>
              )}
            </div>

            {/* Turnover / Liquidity Tier Selector */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <label style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 500 }}>成交额流动性分级:</label>
              <select
                value={turnoverTier}
                onChange={(e) => setTurnoverTier(e.target.value as any)}
                style={{
                  padding: '3px 6px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--bg-surface-elevated)',
                  color: 'var(--text-primary)',
                  fontSize: '11px',
                }}
              >
                <option value="ALL">全部成交额</option>
                <option value="GT50B">&gt; 50 亿 (超级龙头资金)</option>
                <option value="GT10B">&gt; 10 亿 (机构主战场)</option>
                <option value="GT5B">&gt; 5 亿 (高活跃流动性)</option>
                <option value="GT1B">&gt; 1 亿 (主力基本池)</option>
                <option value="LT1B">&lt; 1 亿 (微流动性标的)</option>
              </select>
            </div>

            {/* Pct Change Tier Selector */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <label style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 500 }}>涨跌幅区间 (%):</label>
              <select
                value={pctChangeTier}
                onChange={(e) => setPctChangeTier(e.target.value as any)}
                style={{
                  padding: '3px 6px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--bg-surface-elevated)',
                  color: 'var(--text-primary)',
                  fontSize: '11px',
                }}
              >
                <option value="ALL">全部涨跌幅</option>
                <option value="STRONG_UP">强势领涨 (&gt; +5%)</option>
                <option value="MODERATE_UP">稳健上涨 (+2% ~ +5%)</option>
                <option value="FLAT">震荡平盘 (-2% ~ +2%)</option>
                <option value="DEEP_DOWN">深度回调 (&lt; -5%)</option>
                <option value="LIMIT_UP">仅涨停标的</option>
                <option value="LIMIT_DOWN">仅跌停标的</option>
              </select>
            </div>

            {/* ST Risk Gating */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <label style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 500 }}>ST 风险警示隔离:</label>
              <select
                value={stFilter}
                onChange={(e) => setStFilter(e.target.value as any)}
                style={{
                  padding: '3px 6px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--bg-surface-elevated)',
                  color: 'var(--text-primary)',
                  fontSize: '11px',
                }}
              >
                <option value="EXCLUDE">剔除 ST / *ST 标的 (安全防线)</option>
                <option value="ALL">包含所有标的 (含 ST)</option>
                <option value="ONLY_ST">仅看 ST / *ST (特种重组)</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* 4. Market Sentiment & Summary Ribbon (盘面情绪统计看板) */}
      <div
        className="screener-summary-ribbon"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          padding: '6px 12px',
          background: 'var(--bg-card)',
          borderRadius: '6px',
          border: '1px solid var(--border-card)',
          fontSize: '11px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span>
            筛选池标的: <strong style={{ color: 'var(--text-primary)', fontSize: '12px' }}>{metrics.totalCount}</strong> 只
            <span style={{ color: 'var(--text-muted)', marginLeft: '4px' }}>
              (占库内 {data.length > 0 ? `${Math.floor((metrics.totalCount * 1000) / data.length) / 10}` : '0'}% · 已对齐总池 {totalSymbolsInDb} 只)
            </span>
          </span>

          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ color: colorMode === 'mainland' ? 'var(--color-bearish)' : 'var(--color-bullish)', fontWeight: 600 }}>
              ▲ 上涨: {metrics.upCount}
            </span>
            <span style={{ color: 'var(--text-muted)' }}>|</span>
            <span style={{ color: colorMode === 'mainland' ? 'var(--color-bullish)' : 'var(--color-bearish)', fontWeight: 600 }}>
              ▼ 下跌: {metrics.downCount}
            </span>
            <span style={{ color: 'var(--text-muted)' }}>|</span>
            <span style={{ color: 'var(--text-muted)' }}>平盘: {metrics.flatCount}</span>
          </span>

          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ padding: '1px 5px', borderRadius: '3px', background: 'var(--badge-bearish-bg)', color: 'var(--color-bearish)', fontWeight: 600, fontSize: '10px' }}>
              涨停: {metrics.limitUpCount}
            </span>
            <span style={{ padding: '1px 5px', borderRadius: '3px', background: 'var(--badge-bullish-bg)', color: 'var(--color-bullish)', fontWeight: 600, fontSize: '10px' }}>
              跌停: {metrics.limitDownCount}
            </span>
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: 'var(--text-secondary)' }}>
            当前成交合计: <strong style={{ color: 'var(--text-primary)', fontSize: '12px' }}>{formatVolume(metrics.totalTurnover.toString(), { abbreviate: 'zh' })}</strong>
          </span>
        </div>
      </div>

      {/* 5. Main Quantitative Data Table with Selectable Page Sizes (30 / 50 / 100 / 200) */}
      <QuantitativeDataTable<AShareScreenerItem>
        data={displayData}
        columns={columns}
        state={state}
        errorMessage={errorMessage}
        onRetry={fetchScreenerData}
        pageSize={30}
        pageSizeOptions={[30, 50, 100, 200]}
        rowKey={(r) => r.thscode}
        initialSortKey="turnover"
        initialSortDirection="desc"
        searchFilter={(r, q) =>
          (r.name || r.stock_name || '').toLowerCase().includes(q) ||
          r.thscode.toLowerCase().includes(q)
        }
        searchPlaceholder="按股票名称或代码筛选 (如 茅台 / 巨石 / 600519)..."
        emptyMessage="当前多维筛选条件下无符合条件的标的，请调整筛选区间或重置筛选"
      />
    </div>
  );
};
